import { ref, readonly, h } from 'vue'
import AppModal from '@/components/atoms/AppModal.vue'

import type { Ref, FunctionalComponent, Component } from 'vue'

type ModalType = Component
type StateType = Ref<boolean>
type OperationType = () => void

type ModalName<T extends string> = `${Capitalize<T>}Modal`
type StateKey<T extends string> = `is${ModalName<T>}Opened`
type OpenKey<T extends string> = `open${ModalName<T>}`
type CloseKey<T extends string> = `close${ModalName<T>}`

const capitalize = <T extends string>(value: T): Capitalize<T> =>
  (value.slice(0, 1).toUpperCase() + value.slice(1)) as Capitalize<T>

type DefaultKeys = {
  modalName: 'ControlledModal'
  stateKey: 'isModalOpened'
  openKey: 'openModal'
  closeKey: 'closeModal'
}
type NamedKeys<T extends string> = {
  modalName: ModalName<T>
  stateKey: StateKey<T>
  openKey: OpenKey<T>
  closeKey: CloseKey<T>
}

type Keys<T extends string> =
  ModalName<T> | StateKey<T> | OpenKey<T> | CloseKey<T>

type NamedReturnValue<L extends Keys<string>> = {
  [K in keyof Record<L, unknown>]: K extends StateKey<string>
    ? StateType
    : K extends OpenKey<string>
      ? OperationType
      : K extends CloseKey<string>
        ? OperationType
        : K extends ModalName<string>
          ? ModalType
          : never
}

const DEFAULT_KEYS: DefaultKeys = {
  modalName: 'ControlledModal',
  stateKey: 'isModalOpened',
  openKey: 'openModal',
  closeKey: 'closeModal',
}

const nameKeys = <ModalKey extends string>(
  modalKey: ModalKey | undefined
): typeof modalKey extends undefined ? DefaultKeys : NamedKeys<ModalKey> => {
  if (modalKey === undefined) {
    return DEFAULT_KEYS as any // eslint-disable-line @typescript-eslint/no-explicit-any
  }

  const modalName = `${capitalize(modalKey)}Modal`
  const stateKey = `is${modalName}Opened`
  const openKey = `open${modalName}`
  const closeKey = `close${modalName}`

  return {
    modalName,
    stateKey,
    openKey,
    closeKey,
  } as any // eslint-disable-line @typescript-eslint/no-explicit-any
}

// useModalState
export const useModalState = () => {
  const state = ref(false)
  const isModalOpened = readonly(state)
  const openModal = () => {
    state.value = true
  }
  const closeModal = () => {
    state.value = false
  }

  return {
    isModalOpened,
    openModal,
    closeModal,
  }
}

// useModal
type ModalDefaultReturnValue = {
  ControlledModal: ModalType
  openModal: OperationType
  closeModal: OperationType
}

type ModalNamedReturnValue<T extends string> = NamedReturnValue<
  ModalName<T> | OpenKey<T> | CloseKey<T>
>

type UseModal = {
  (): ModalDefaultReturnValue
  <Key extends string>(modalKey: Key): ModalNamedReturnValue<Key>
  <Key extends string>(
    modalKey: Key,
    modalComponent: Component
  ): ModalNamedReturnValue<Key>
}

export const useModal: UseModal = <ModalKey extends string>(
  modalKey?: ModalKey,
  modalComponent: Component = AppModal
) => {
  const { isModalOpened, openModal, closeModal } = useModalState()

  const component: FunctionalComponent = (_, { attrs, slots }) => {
    const onClose = () => {
      closeModal()
      ;(attrs?.onClose as () => void)?.()
    }
    return h(
      modalComponent as any, // eslint-disable-line @typescript-eslint/no-explicit-any
      { ...attrs, shown: isModalOpened.value, onClose },
      slots
    )
  }

  const { modalName, openKey, closeKey } = nameKeys(modalKey)

  return {
    [modalName]: component,
    [openKey]: openModal,
    [closeKey]: closeModal,
  } as any // eslint-disable-line @typescript-eslint/no-explicit-any
}
